<script setup lang="ts">
import { Egg } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
</script>

<template>
  <div class="app-loader" role="status" :aria-live="'polite'" :aria-label="t('common.loading')">
    <div class="app-loader__content">
      <div class="app-loader__badge">
        <span class="app-loader__ring" aria-hidden="true" />
        <span class="app-loader__mark">
          <Egg :size="26" />
        </span>
      </div>
      <div class="app-loader__text">
        <strong>{{ t('auth.brandLabel') }}</strong>
        <small>{{ t('common.loading') }}</small>
      </div>
      <span class="app-loader__dots" aria-hidden="true">
        <i /><i /><i />
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.app-loader {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  place-items: center;
  background: var(--color-page);
  padding: 24px;
}

.app-loader__content {
  display: grid;
  justify-items: center;
  gap: 20px;
}

.app-loader__badge {
  position: relative;
  display: grid;
  width: 78px;
  height: 78px;
  place-items: center;
}

.app-loader__ring {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  border: 3px solid color-mix(in srgb, var(--color-brand) 16%, transparent);
  border-top-color: var(--color-brand);
  animation: app-loader-spin 820ms linear infinite;
}

.app-loader__mark {
  display: inline-grid;
  width: 56px;
  height: 56px;
  place-items: center;
  border-radius: 18px;
  background: var(--color-brand);
  color: var(--color-text-inverse);
  box-shadow: 0 16px 40px color-mix(in srgb, var(--color-brand) 34%, transparent);
  animation: app-loader-pulse 1.9s var(--ease-standard) infinite;
}

.app-loader__text {
  display: grid;
  justify-items: center;
  gap: 4px;
  text-align: center;

  strong {
    color: var(--color-text);
    font-size: 18px;
    font-weight: 900;
    letter-spacing: 0.02em;
  }

  small {
    color: var(--color-text-muted);
    font-size: 13px;
    font-weight: 700;
  }
}

.app-loader__dots {
  display: inline-flex;
  gap: 6px;

  i {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--color-brand) 55%, transparent);
    animation: app-loader-bounce 1.2s var(--ease-standard) infinite;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
}

@keyframes app-loader-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes app-loader-pulse {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
    box-shadow: 0 20px 48px color-mix(in srgb, var(--color-brand) 44%, transparent);
  }
}

@keyframes app-loader-bounce {
  0%,
  100% {
    opacity: 0.4;
    transform: translateY(0);
  }

  40% {
    opacity: 1;
    transform: translateY(-5px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-loader__ring,
  .app-loader__mark,
  .app-loader__dots i {
    animation-duration: 0.001ms;
    animation-iteration-count: 1;
  }
}
</style>
